import React, { useEffect, useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './TermsConditionsView.css';

import { apiUrl } from '../../../utility/Connections';
import { Layout, Typography, Button, Tag, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import LoadingOverlay from '../../../components/loadingOverlay/loadingOverlay';

const { Content } = Layout;
const { Title, Text } = Typography;

const QUILL_MODULES = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['link'],
        [{ 'align': [] }],
        ['clean'],
    ],
};

const QUILL_FORMATS = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'align',
];

const TermsConditionsView = () => {
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [content, setContent] = useState('');
    const [currentVersion, setCurrentVersion] = useState('2.0');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        loadContent();
    }, []);

    const loadContent = () => {
        setShowLoading(true);
        setLoadingText('Cargando contenido de Términos y Condiciones...');

        const token = localStorage.getItem('access_token');
        const headers = { headers: { Authorization: `Bearer ${token}` } };

        axios.get(`${apiUrl}/terms-conditions/get-content/`, headers)
            .then((response) => {
                const { code, data } = response.data;
                if (code === 'SUCCESS' && data) {
                    setContent(data.tcc_content || '');
                    setCurrentVersion(data.tcc_version || '2.0');
                    setLastSavedAt(data.tcc_updated_at);
                }
                setShowLoading(false);
            })
            .catch((error) => {
                console.error('Error loading TyC content:', error);
                setShowLoading(false);
            });
    };

    const handleSave = () => {
        if (!content || content === '<p><br></p>') {
            withReactContent(Swal).fire({
                title: 'Atención',
                icon: 'warning',
                text: 'El contenido de TyC no puede estar vacío.',
                confirmButtonColor: '#012E40',
            });
            return;
        }

        setShowLoading(true);
        setLoadingText('Guardando contenido de Términos y Condiciones...');

        const token = localStorage.getItem('access_token');
        const headers = { headers: { Authorization: `Bearer ${token}` } };

        axios.post(`${apiUrl}/terms-conditions/save-content/`, {
            tcc_content: content,
            tcc_version: currentVersion,
        }, headers)
            .then((response) => {
                const { code, data } = response.data;
                if (code === 'SUCCESS') {
                    setLastSavedAt(data.tcc_updated_at);
                    setHasUnsavedChanges(false);
                    withReactContent(Swal).fire({
                        title: 'Guardado',
                        icon: 'success',
                        text: 'El contenido de TyC se guardó correctamente.',
                        confirmButtonColor: '#012E40',
                    });
                }
                setShowLoading(false);
            })
            .catch((error) => {
                console.error('Error saving TyC content:', error);
                withReactContent(Swal).fire({
                    title: 'Error',
                    icon: 'error',
                    text: 'Ocurrió un error al guardar el contenido.',
                    confirmButtonColor: '#012E40',
                });
                setShowLoading(false);
            });
    };

    const handleContentChange = (value) => {
        setContent(value);
        setHasUnsavedChanges(true);
    };

    return (
        <div className='main-container'>
            {showLoading && <LoadingOverlay text={loadingText} showProgress={false} progress={null} />}
            <Content className='main-subcontainer main-subcontainer-column'>
                <div className='layout-content'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>Términos y Condiciones</Title>
                            <Text type='secondary'>
                                Edita el texto de autorización de tratamiento de datos personales que se muestra antes de cada encuesta.
                            </Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {hasUnsavedChanges && <Tag color='orange'>Cambios sin guardar</Tag>}
                            <Tag color='blue'>Versión {currentVersion}</Tag>
                            <Button
                                type='primary'
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                disabled={!hasUnsavedChanges}
                            >
                                Guardar
                            </Button>
                        </div>
                    </div>

                    <div className='tyc-editor-info'>
                        <Text type='secondary' style={{ fontSize: 12 }}>
                            <strong>Nota:</strong> Donde se escriba <code>[NOMBRE DE LA EMPRESA CLIENTE]</code>, se reemplazará automáticamente con el nombre de la empresa de la medición activa.
                            Donde se escriba <code>www.mentoringconsultores.com/politica-de-datos</code> se convertirá en un enlace clickeable.
                        </Text>
                    </div>

                    <div className='tyc-editor-container'>
                        <ReactQuill
                            theme='snow'
                            value={content}
                            onChange={handleContentChange}
                            modules={QUILL_MODULES}
                            formats={QUILL_FORMATS}
                            placeholder='Escribe aquí el texto de autorización de tratamiento de datos personales...'
                        />
                    </div>

                    {lastSavedAt && (
                        <div style={{ marginTop: 12, textAlign: 'right' }}>
                            <Text type='secondary' style={{ fontSize: 12 }}>
                                Última actualización: {new Date(lastSavedAt).toLocaleString('es-CO')}
                            </Text>
                        </div>
                    )}
                </div>
            </Content>
        </div>
    );
};

export default TermsConditionsView;
